import { Button, DialogRoot, DialogTrigger } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UserGroupListItem } from '@/entities/userGroups';
import { NestedItemCard } from '@/features/nested-items';
import { UserFormValues } from '../UserForm/types';
import { AddUserGroupsDialog } from './AddUserGroupsDialog';

type Props = {
  disabled?: boolean;
};

export const UserGroupsList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['users', 'common']);
  const { setValue, getValues, watch } = useFormContext<UserFormValues>();
  const currentGroups = watch('userGroups');

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCancelDialog = () => setIsDialogOpen(false);

  const handleSaveDialog = (newGroups: UserGroupListItem[]) => {
    const addedGroups = newGroups.map((group) => ({ id: group.id, name: group.name }));
    setValue('userGroups', [...(getValues('userGroups') || []), ...addedGroups]);
    setIsDialogOpen(false);
  };

  const handleRemoveGroup = (id: number) => {
    setValue('userGroups', getValues('userGroups')?.filter((group) => group.id !== id) || []);
  };

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('group')}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenDialog}>
                {t('addGroup')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {currentGroups.map((group) => (
            <NestedItemCard
              key={group.id}
              item={group}
              renderTitle={(item) => item.name}
              action={
                !disabled && (
                  <IconButton
                    icon={<XCircleIcon />}
                    variant="ghost"
                    color="destructive"
                    label={t('users:removeGroupFromUser')}
                    onClick={() => handleRemoveGroup(group.id)}
                  />
                )
              }
            />
          ))}
          {currentGroups.length === 0 && <p className="text-sm text-muted-foreground">{t('noGroupsInUser')}</p>}
        </div>
      </div>

      <AddUserGroupsDialog
        existingGroupIds={currentGroups?.map((group) => group.id) || []}
        onCancel={handleCancelDialog}
        onSave={handleSaveDialog}
        className="max-w-160"
      />
    </DialogRoot>
  );
};
